/* The board fills the height left under the header so columns scroll
   internally instead of growing the page. */
.board {
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	gap: var(--space-3);
}

/* Horizontal lane scroller. Snap makes the mobile one-up swipe land cleanly
   on a single column; on desktop several lanes show and snap to their start.
   Scrollbar hidden to match the rest of the app. */
.scroller {
	flex: 1;
	min-height: 0;
	display: flex;
	gap: var(--space-3);
	overflow-x: auto;
	scroll-snap-type: x mandatory;
	overscroll-behavior-x: contain;
	-webkit-overflow-scrolling: touch;
	scrollbar-width: none;
}

.scroller::-webkit-scrollbar {
	display: none;
}

/* One lane per column. Full-width on mobile (one-up), fixed 300px on wider
   screens so multiple lanes sit side-by-side and scroll past the viewport. */
.snap {
	flex: 0 0 100%;
	min-width: 0;
	height: 100%;
	display: flex;
	scroll-snap-align: start;
}

@media (min-width: 640px) {
	.snap {
		flex-basis: 300px;
	}
}

.column {
	flex: 1;
	min-width: 0;
	height: 100%;
	display: flex;
	flex-direction: column;
	background: var(--surface-input);
	border-radius: var(--radius-md);
	padding: var(--space-3);
}

/* Priority-only view: a single column, full-width on mobile and a centred
   max-width lane on wider screens (a lone 300px lane would look lost). Fills
   the height under the header like .board does. */
.priorityWrap {
	flex: 1;
	min-height: 0;
	display: flex;
	justify-content: center;
}

.priorityWrap .column {
	width: 100%;
	max-width: 480px;
}

.colHeader {
	display: flex;
	align-items: center;
	gap: var(--space-2);
	padding: 0 var(--space-1) var(--space-2);
}

/* Per-category sub-heading inside the priority column — lighter than a lane's
   own .colHeader. */
.subHeader {
	display: flex;
	align-items: center;
	gap: var(--space-2);
	padding: var(--space-2) var(--space-1) var(--space-1);
}

.subName {
	margin: 0;
	font-size: 0.75rem;
	font-weight: 600;
	text-transform: lowercase;
	letter-spacing: 0.03em;
	color: var(--ink-muted);
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.dot {
	width: 10px;
	height: 10px;
	border-radius: 50%;
	flex: none;
}

.colName {
	margin: 0;
	font-size: 0.875rem;
	font-weight: 600;
	text-transform: lowercase;
	letter-spacing: 0.02em;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.count {
	margin-left: auto;
	font-size: 0.75rem;
	color: var(--ink-muted);
}

/* Internally-scrolling card stack. */
.cards {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	overscroll-behavior-y: contain;
	-webkit-overflow-scrolling: touch;
	display: flex;
	flex-direction: column;
	gap: var(--space-2);
	padding-bottom: var(--space-3);
	scrollbar-width: none;
}

.cards::-webkit-scrollbar {
	display: none;
}

.list {
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: var(--space-2);
}

/* Collapsible done area at the bottom of a lane. */
.done {
	display: flex;
	flex-direction: column;
	gap: var(--space-2);
	margin-top: var(--space-2);
}

.doneToggle {
	display: flex;
	align-items: center;
	gap: var(--space-1);
	align-self: flex-start;
	background: none;
	border: none;
	color: var(--ink-muted);
	font: inherit;
	font-size: 0.8125rem;
	font-weight: 600;
	cursor: pointer;
	padding: var(--space-1) 0;
}

.doneToggle:hover {
	color: var(--ink);
}

.clear {
	align-self: center;
	background: none;
	border: none;
	color: var(--ink-muted);
	font-size: 0.8125rem;
	cursor: pointer;
	padding: var(--space-2);
	border-radius: var(--radius-sm);
}

.clear:hover:not(:disabled) {
	color: var(--ink);
}

.clear:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.colEmpty {
	color: var(--ink-muted);
	font-size: 0.875rem;
	text-align: center;
	padding: var(--space-4) 0;
	margin: 0;
}

/* Mobile-only page indicator for the swipeable lanes. */
.pager {
	display: flex;
	justify-content: center;
	gap: var(--space-2);
	padding: var(--space-1) 0;
}

@media (min-width: 640px) {
	.pager {
		display: none;
	}
}

.pagerDot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	border: none;
	padding: 0;
	cursor: pointer;
	transition: transform 120ms ease;
}

.pagerDot[aria-current='true'] {
	transform: scale(1.35);
}
